import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShoppingBag, Users, CreditCard, FileText, Package, TrendingUp,
  BarChart2, Settings, ChevronDown, MoreHorizontal, RefreshCw, Plus
} from 'lucide-react';

import OverviewTab from './components/OverviewTab';
import CustomersTab from './components/CustomersTab';
import CreditCollectionsTab from './components/CreditCollectionsTab';
import OrdersTab from './components/OrdersTab';
import InventoryATPTab from './components/InventoryATPTab';
import PlanningForecastingTab from './components/PlanningForecastingTab';
import ReportsTab from './components/ReportsTab';
import SettingsTab from './components/SettingsTab';

const SalesDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const mainTabs = [
    { id: 'overview', label: 'Overview', icon: BarChart2, description: 'Executive dashboard & KPIs' },
    { id: 'customers', label: 'Customers', icon: Users, description: 'B2B customer profiles & contracts' },
    { id: 'credit-collections', label: 'Credit & Collections', icon: CreditCard, description: 'Credit limits, aging, SOA' },
    { id: 'orders', label: 'Orders', icon: FileText, description: 'Quote to order & tracking' },
    { id: 'inventory-atp', label: 'Inventory & ATP', icon: Package, description: 'Available-to-promise & substitutions' },
    { id: 'planning-forecasting', label: 'Planning & Forecasting', icon: TrendingUp, description: 'Demand & consumption alerts' },
  ];

  const moreTabs = [
    { id: 'reports', label: 'Reports & Analytics', icon: BarChart2, description: 'Sales reports & trends' },
    { id: 'settings', label: 'Settings', icon: Settings, description: 'Sales module configuration' },
  ];

  const allTabs = [...mainTabs, ...moreTabs];

  useEffect(() => {
    const path = window.location.pathname;
    const lastSegment = path.split('/').filter(Boolean).pop();
    if (lastSegment && lastSegment !== 'sales' && allTabs.some(t => t.id === lastSegment)) {
      setActiveTab(lastSegment);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowMoreDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    setShowMoreDropdown(false);
    navigate(`/sales/${tabId}`);
  };

  return (
    <div className="space-y-6">
      {/* Header - matches Supply Chain / Financial style */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-700 rounded-xl p-6 text-white">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold">Sales Management</h1>
            <p className="text-teal-100 mt-1">
              B2B sales, credit, orders, and supply chain–connected workflows
            </p>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <button className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 hover:bg-white/20 transition-all text-white text-sm font-medium flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
            <button className="px-4 py-2 bg-white text-teal-700 rounded-lg text-sm font-medium hover:bg-teal-50 transition-all flex items-center gap-2">
              <Plus className="h-4 w-4" />
              New Order
            </button>
          </div>
        </div>

        {/* Centered Tabs */}
        <div className="mt-6 flex justify-center">
          <div className="inline-flex flex-wrap justify-center gap-1 bg-white/10 rounded-lg p-1 backdrop-blur-sm">
            {mainTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id ? 'bg-white text-teal-700' : 'text-white/90 hover:bg-white/10'
                  }`}
                  title={tab.description}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowMoreDropdown(!showMoreDropdown)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  moreTabs.some(t => t.id === activeTab) ? 'bg-white text-teal-700' : 'text-white/90 hover:bg-white/10'
                }`}
              >
                <MoreHorizontal className="w-4 h-4" />
                More
                <ChevronDown className={`w-3 h-3 transition-transform ${showMoreDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showMoreDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute top-full left-0 mt-2 w-52 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                >
                  {moreTabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => handleTabClick(tab.id)}
                        className={`w-full flex items-center gap-3 px-4 py-2 text-sm font-medium transition-colors ${
                          activeTab === tab.id ? 'bg-teal-50 text-teal-700' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        title={tab.description}
                      >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Routes>
        <Route path="" element={<OverviewTab />} />
        <Route path="overview" element={<OverviewTab />} />
        <Route path="customers" element={<CustomersTab />} />
        <Route path="credit-collections" element={<CreditCollectionsTab />} />
        <Route path="orders" element={<OrdersTab />} />
        <Route path="inventory-atp" element={<InventoryATPTab />} />
        <Route path="planning-forecasting" element={<PlanningForecastingTab />} />
        <Route path="reports" element={<ReportsTab />} />
        <Route path="settings" element={<SettingsTab />} />
      </Routes>
    </div>
  );
};

export default SalesDashboard;
